import _ from 'lodash'
import {
    DalItem,
    DalJsNamespace,
    DalJsStore,
    ExtensionAPI,
    Null,
    PAGE_FRAGMENT_TYPE,
    SnapshotDal
} from '@wix/document-manager-core'
import type {SnapshotExtApi} from '../../snapshots'
import {type PossibleSyncTags, SYNC_TAGS} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'
import {GLOBAL_NAMESPACES} from '../../import-export/json/constants'
import {MASTER_PAGE_ID} from '../../../constants/constants'
import type {FragmentsExtensionAPI} from '../../fragments/fragments'

type SnapshotIdsByTag = Record<PossibleSyncTags, Set<SnapshotDal['id']>>

export class SnapshotSyncManager {
    private syncedSnapshotsBySource: SnapshotIdsByTag = {
        [SYNC_TAGS.EDITOR_SYNC_TAG]: new Set(),
        [SYNC_TAGS.FS_SYNC_TAG]: new Set()
    }
    constructor() {}

    private markSnapshotSynced = (tag: PossibleSyncTags, snapshot: SnapshotDal) => {
        this.syncedSnapshotsBySource[tag].add(snapshot.id)
    }
    private isSyncedFromTag = (tag: PossibleSyncTags, snapshot: SnapshotDal): boolean => {
        return this.syncedSnapshotsBySource[tag].has(snapshot.id)
    }
    public markSnapshotSyncedFromFS = (snapshot: SnapshotDal) => {
        this.markSnapshotSynced(SYNC_TAGS.FS_SYNC_TAG, snapshot)
    }
    public markSnapshotSyncedFromEditor = (snapshot: SnapshotDal) => {
        this.markSnapshotSynced(SYNC_TAGS.EDITOR_SYNC_TAG, snapshot)
    }
    public isSyncedFromEditor = (snapshot: SnapshotDal) => this.isSyncedFromTag(SYNC_TAGS.EDITOR_SYNC_TAG, snapshot)
    public isSyncedFromFS = (snapshot: SnapshotDal) => this.isSyncedFromTag(SYNC_TAGS.FS_SYNC_TAG, snapshot)
}

const getOldValue = (prev: Null<SnapshotDal>, namespace: string, id: string) => prev?.getValue({type: namespace, id})

const getId = ({id}: DalItem): string | undefined => id

const getPageIdFromMetadata = ({metaData}: DalItem): string | undefined => metaData?.pageId

const PAGE_ID_SPECIAL_CASES = {
    data: {
        AppPage: getId,
        Page: getId
    }
}

const getPageId = (namespace: string, val?: DalItem): string | undefined => {
    if (!val) {
        return
    }

    const getPageIdFunc = PAGE_ID_SPECIAL_CASES[namespace]?.[val.type!] ?? getPageIdFromMetadata
    return getPageIdFunc(val)
}

const itemToBeSyncedInGlobalNamespaces = (namespace: string, id: string): boolean => {
    const globalNamespace = GLOBAL_NAMESPACES[namespace]
    if (!globalNamespace) {
        return false
    }

    return globalNamespace === 'All' || globalNamespace.has(id)
}

const getChangedPagesBetweenSnapshots = (currentSnapshot: SnapshotDal, fromSnapshot: SnapshotDal): Set<string> => {
    const pageIds: Set<string> = new Set()
    const changes: DalJsStore = currentSnapshot.diff(fromSnapshot, PAGE_FRAGMENT_TYPE)
    _.forOwn(changes, (items: DalJsNamespace, namespace: string) => {
        _.forOwn(items, (value: DalItem | undefined, id: string) => {
            const oldValue = getOldValue(fromSnapshot, namespace, id)
            const oldPage: string | undefined = getPageId(namespace, oldValue)
            const newPage: string | undefined = getPageId(namespace, value)

            if (!oldPage && !newPage && itemToBeSyncedInGlobalNamespaces(namespace, id)) {
                pageIds.add(MASTER_PAGE_ID)
            }

            if (oldPage) pageIds.add(oldPage)
            if (newPage) pageIds.add(newPage)
        })
    })
    return pageIds
}

const getChangedFragmentsBetweenSnapshots = (
    currentSnapshot: SnapshotDal,
    fromSnapshot: SnapshotDal,
    extensionAPI: ExtensionAPI
): Set<string> => {
    const changedFragmentTypes: Set<string> = new Set()
    const {fragments} = extensionAPI as FragmentsExtensionAPI
    const fragmentTypes: string[] = fragments.getAllFragmentTypes()

    for (const fragmentType of fragmentTypes) {
        const changes: DalJsStore = (currentSnapshot as any).diff(fromSnapshot, fragmentType)
        const hasChanges = Object.keys(changes ?? {}).length > 0
        if (hasChanges) {
            changedFragmentTypes.add(fragmentType)
        }
    }

    return changedFragmentTypes
}

const applyOperationOnSnapshotDelta = (
    extensionAPI: ExtensionAPI,
    snapshotSyncManager: SnapshotSyncManager,
    addUpdatedIds: (
        currentSnapshot: SnapshotDal,
        previousSnapshot: SnapshotDal,
        isFsSyncTaggedSnapshot: boolean
    ) => void
) => {
    const {snapshots} = extensionAPI as SnapshotExtApi
    let currentSnapshot: SnapshotDal = snapshots.getCurrentSnapshot()

    while (!snapshotSyncManager.isSyncedFromEditor(currentSnapshot)) {
        const previousSnapshot = currentSnapshot.getPreviousSnapshot()

        // we met initial snapshot and no editor sync tag found
        // nothing to sync, we probably in broken flow
        if (previousSnapshot === null) {
            throw new ReportableError({
                errorType: 'invalidSnapshotSequence',
                message: 'Initial snapshot reached without meeting editor sync tag'
            })
        }

        const isFsSyncTaggedSnapshot = snapshotSyncManager.isSyncedFromFS(currentSnapshot)
        addUpdatedIds(currentSnapshot, previousSnapshot, isFsSyncTaggedSnapshot)
        currentSnapshot = previousSnapshot
    }
}

export const getUpdatedPagesAndFragments = (
    extensionAPI: ExtensionAPI,
    snapshotSyncManager: SnapshotSyncManager
): {pageIds: string[]; fragmentTypes: string[]} => {
    const calculatedPagesMap: Map<string, boolean> = new Map()
    const calculatedFragmentsMap: Map<string, boolean> = new Map()

    applyOperationOnSnapshotDelta(extensionAPI, snapshotSyncManager, (current, previous, isFsTagged) => {
        const currentChangedPages = getChangedPagesBetweenSnapshots(current, previous)
        // remember pages that were synced already from fs
        // add to list of changed pages only those that not already synced (overwritten) by fs sync
        currentChangedPages.forEach(pageId => {
            if (!calculatedPagesMap.has(pageId)) {
                calculatedPagesMap.set(pageId, !isFsTagged)
            }
        })

        const changedFragmentTypes = getChangedFragmentsBetweenSnapshots(current, previous, extensionAPI)
        for (const fragmentType of changedFragmentTypes) {
            if (!calculatedFragmentsMap.has(fragmentType)) {
                calculatedFragmentsMap.set(fragmentType, !isFsTagged)
            }
        }
    })

    const pageIds: string[] = []
    calculatedPagesMap.forEach((isIncluded, pageId) => {
        if (isIncluded) pageIds.push(pageId)
    })

    const fragmentTypes: string[] = []
    calculatedFragmentsMap.forEach((isIncluded, type) => {
        if (isIncluded) fragmentTypes.push(type)
    })

    return {pageIds, fragmentTypes}
}
